import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TopBar } from "@/components/layout/TopBar";
import { altioraToast } from "@/components/altiora/toast";
import { KeyRound, PlusCircle, Copy, Trash2, AlertTriangle } from "lucide-react";

export default function EnterpriseAPI() {
  const { lang } = useI18n();
  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [newName, setNewName] = useState("");
  const [newScopes, setNewScopes] = useState([]);
  const [issued, setIssued] = useState(null); // raw key returned once

  const load = async () => {
    try {
      const [k, s] = await Promise.all([
        api.get("/enterprise-api/keys"),
        api.get("/enterprise-api/scopes"),
      ]);
      setKeys(k.data.items || []);
      setScopes(s.data.scopes || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const toggleScope = (s) => {
    setNewScopes((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
  };

  const issue = async () => {
    if (!newName.trim() || newScopes.length === 0) {
      altioraToast.error(lang === "fr" ? "Nom et scopes requis" : "Name and scopes required");
      return;
    }
    try {
      const { data } = await api.post("/enterprise-api/keys", { name: newName, scopes: newScopes });
      setIssued(data);
      setNewName("");
      setNewScopes([]);
      await load();
      altioraToast.success(lang === "fr" ? "Clé créée — copiez-la maintenant" : "Key issued — copy it now");
    } catch {
      altioraToast.error(lang === "fr" ? "Erreur de création" : "Create error");
    }
  };

  const revoke = async (id) => {
    if (!confirm(lang === "fr" ? "Révoquer cette clé définitivement ?" : "Revoke this key permanently?")) return;
    await api.delete(`/enterprise-api/keys/${id}`);
    await load();
  };

  const copy = async (val) => {
    await navigator.clipboard.writeText(val).catch(() => {});
    altioraToast.success(lang === "fr" ? "Copié" : "Copied");
  };

  return (
    <div className="min-h-full" data-testid="enterprise-api-root">
      <TopBar
        title="Enterprise API"
        subtitle={lang === "fr" ? "Clés API partenaires — scopes, révocation, journal." : "Partner API keys — scopes, revocation, audit."}
      />
      <div className="px-6 md:px-10 py-8 md:py-10 space-y-8">
        {/* Newly issued key banner */}
        {issued && (
          <div className="rounded-md border border-[#D4AF37]/50 bg-[#D4AF37]/10 p-5" data-testid="enterprise-api-issued">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-[#D4AF37] mt-0.5" strokeWidth={1.8} />
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#D4AF37]">
                  {lang === "fr" ? "Clé émise une seule fois" : "Key shown once"}
                </div>
                <div className="mt-1 text-sm text-[#1F2937]">{issued.name}</div>
                <div className="mt-3 flex items-center gap-2">
                  <code className="flex-1 bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-xs font-mono text-[#0B2545] truncate">
                    {issued.raw_key}
                  </code>
                  <button
                    onClick={() => copy(issued.raw_key)}
                    className="inline-flex items-center gap-1 text-xs text-[#0B2545] hover:text-[#DCC29A] px-3 py-2 border border-[#0B2545]/25 rounded-md"
                  >
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.8} />
                    {lang === "fr" ? "Copier" : "Copy"}
                  </button>
                </div>
                <div className="mt-2 text-[10px] text-[#5B5B62] font-mono">
                  {issued.note}
                </div>
                <button
                  onClick={() => setIssued(null)}
                  className="mt-3 text-[10px] text-[#5B5B62] hover:text-[#1F2937] font-mono uppercase tracking-widest"
                >
                  {lang === "fr" ? "J'ai copié la clé" : "I've copied the key"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Issue new key */}
        <div className="rounded-md border border-[#E4E1D8] bg-white p-6">
          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#0B2545] mb-2">
            {lang === "fr" ? "Nouvelle clé API" : "New API key"}
          </div>
          <div className="font-display text-lg text-[#1F2937] tracking-tight mb-6">
            {lang === "fr" ? "Émettre une clé" : "Issue a key"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <input
              data-testid="enterprise-api-name"
              type="text"
              placeholder={lang === "fr" ? "Nom (ex. Prod integration)" : "Name (e.g. Prod integration)"}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="md:col-span-2 bg-[#F8F7F3] border border-[#E4E1D8] rounded-md px-3 py-2 text-sm text-[#1F2937] focus:border-[#0B2545] focus:outline-none"
            />
            <button
              data-testid="enterprise-api-issue"
              onClick={issue}
              className="inline-flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#DCC29A] text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
            >
              <PlusCircle className="h-4 w-4" strokeWidth={1.8} />
              {lang === "fr" ? "Émettre" : "Issue"}
            </button>
          </div>
          <div className="mt-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-3">Scopes</div>
            <div className="flex flex-wrap gap-2">
              {scopes.map((s) => {
                const active = newScopes.includes(s);
                return (
                  <button
                    key={s}
                    data-testid={`enterprise-api-scope-${s}`}
                    onClick={() => toggleScope(s)}
                    className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded border transition-all ${
                      active
                        ? "bg-[#0B2545]/20 border-[#0B2545] text-[#0B2545]"
                        : "bg-[#F8F7F3] border-[#E4E1D8] text-[#5B5B62] hover:border-[#B8B5AC]"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Existing keys */}
        <div>
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="font-display text-xl tracking-tight text-[#1F2937]">
              {lang === "fr" ? "Clés actives" : "Active keys"}
            </h2>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#5B5B62]">
              {keys.length} · {lang === "fr" ? "actives" : "active"}
            </div>
          </div>
          {keys.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#E4E1D8] bg-[#F8F7F3]/40 p-10 text-center">
              <KeyRound className="h-6 w-6 text-[#5B5B62] mx-auto" strokeWidth={1.5} />
              <div className="mt-3 text-sm text-[#5B5B62]">
                {lang === "fr" ? "Aucune clé active." : "No active keys."}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {keys.map((k) => (
                <div key={k.key_id} data-testid="enterprise-api-key-row" className="rounded-md border border-[#E4E1D8] bg-white px-5 py-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1F2937]">{k.name}</div>
                    <div className="mt-1 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">
                      <span className="text-[#0B2545]">{k.prefix}…</span>
                      <span className="text-[#5B5B62]">·</span>
                      <span>{(k.scopes || []).join(" · ")}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => revoke(k.key_id)}
                    className="text-[#8B0000] hover:text-[#D97706] p-1"
                  >
                    <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
